import { DatePicker, Flex, Select } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../../hooks/use-app-dispatch"
import { useAppSelector } from "../../../../hooks/use-app-selector"
import { DEFAULT_WINDOW_PRESET, setWindow } from "../../redux/portfolio.slice"
import type {
  PortfolioWindow,
  PortfolioWindowPreset
} from "../../redux/portfolio.types"

const PRESET_VALUES: PortfolioWindowPreset[] = [
  "today",
  "this_week",
  "this_month",
  "this_quarter",
  "ytd",
  "last_month",
  "last_quarter",
  "last_year",
  "custom"
]

interface TimeWindowControlProps {
  selectedPreset?: PortfolioWindowPreset
  onSelectedPresetChange?: (preset: PortfolioWindowPreset) => void
}

export const TimeWindowControl = ({
  selectedPreset: controlledSelectedPreset,
  onSelectedPresetChange
}: TimeWindowControlProps = {}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const window = useAppSelector((s) => s.portfolio.window)

  const [internalSelectedPreset, setInternalSelectedPreset] =
    useState<PortfolioWindowPreset>(window.preset)
  const selectedPreset = controlledSelectedPreset ?? internalSelectedPreset

  const selectPreset = (preset: PortfolioWindowPreset) => {
    setInternalSelectedPreset(preset)
    onSelectedPresetChange?.(preset)
  }

  const presets = PRESET_VALUES.map((value) => ({
    label: t(`portfolio.window.${value}`),
    value
  }))

  const commitWindow = (next: PortfolioWindow) => {
    dispatch(setWindow(next))
  }

  const handlePresetChange = (preset: PortfolioWindowPreset) => {
    selectPreset(preset)
    if (preset === "custom") return
    commitWindow({ preset })
  }

  const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates || !dates[0] || !dates[1]) {
      selectPreset(DEFAULT_WINDOW_PRESET)
      commitWindow({ preset: DEFAULT_WINDOW_PRESET })
      return
    }
    const next: PortfolioWindow = {
      preset: "custom",
      start: dates[0].format("YYYY-MM-DD"),
      end: dates[1].format("YYYY-MM-DD")
    }
    commitWindow(next)
  }

  const rangeValue: [Dayjs, Dayjs] | undefined =
    window.preset === "custom" && window.start && window.end
      ? [dayjs(window.start), dayjs(window.end)]
      : undefined

  return (
    <Flex gap="small" align="center" wrap="wrap">
      <Select<PortfolioWindowPreset>
        value={selectedPreset}
        options={presets}
        onChange={handlePresetChange}
        // A short menu; virtual scrolling only hides its tail from a test.
        virtual={false}
        style={{ minWidth: 160 }}
      />
      {selectedPreset === "custom" && (
        <DatePicker.RangePicker
          value={rangeValue}
          onChange={handleRangeChange}
        />
      )}
    </Flex>
  )
}
